import React from "react";

/**
 * CardCtaSection
 *
 * Renders the CTA footer for a card using pre-resolved CTA data from `resolveCtaData()`.
 * Supports two layout variants to match the two card views:
 *
 *  - "detail"  → DetailCard style: horizontal layout with a "Learn more" button on the right,
 *                border-top separator, wider padding. Used in the card detail view.
 *  - "card"    → NewSingleCard style: full-width clickable link strip, border-bottom separator,
 *                compact padding. Used in the card list/grid view.
 *
 * @param {{ cta: object, variant: "detail"|"card", actualTheme: string }} props
 *   cta         — return value of resolveCtaData() (must be non-null for component to render)
 *   variant     — layout style ("detail" | "card"), defaults to "card"
 *   actualTheme — "dark" | "light"
 */
export default function CardCtaSection({ cta, variant = "card", actualTheme }) {
  if (!cta) return null;

  const { linkHref, domain, headline, description, buttonText } = cta;
  const isDark = actualTheme === "dark";

  // ── "detail" variant — used in DetailCard ────────────────────────────
  if (variant === "detail") {
    return (
      <div
        className={`p-3.5 border-t ${
          isDark ? "bg-[#111111] border-white/10" : "bg-gray-50 border-gray-200"
        }`}
      >
        <div className="flex items-center justify-between gap-4">
          <div className="flex-1 min-w-0">
            {domain && (
              <div
                className={`text-[11px] uppercase tracking-wide font-medium mb-1 ${
                  isDark ? "text-gray-500" : "text-gray-500"
                }`}
              >
                {domain}
              </div>
            )}
            {headline && (
              <h3
                className={`text-[14px] font-bold mb-0.5 leading-tight line-clamp-1 ${
                  isDark ? "text-white" : "text-gray-900"
                }`}
              >
                {headline}
              </h3>
            )}
            {description && (
              <p
                className={`text-[12px] leading-snug line-clamp-1 ${
                  isDark ? "text-gray-400" : "text-gray-600"
                }`}
              >
                {description}
              </p>
            )}
          </div>
          <div className="flex-shrink-0">
            <a
              href={linkHref}
              target="_blank"
              rel="noopener noreferrer"
              className={`px-4 py-2 flex items-center justify-center rounded-lg text-[13px] font-bold transition-all no-underline shadow-sm ${
                isDark
                  ? "bg-white/5 border border-white/10 text-white hover:bg-white/10"
                  : "bg-[#F0F2F5] text-gray-900 hover:bg-gray-200 border border-gray-300/50"
              }`}
            >
              {buttonText || "Learn more"}
            </a>
          </div>
        </div>
      </div>
    );
  }

  // ── "card" variant — used in NewSingleCard ────────────────────────────
  return (
    <a
      href={linkHref}
      target="_blank"
      rel="noopener noreferrer"
      className="block no-underline"
    >
      <div
        className={`px-2 py-2 border-b space-y-0.5 ${
          isDark
            ? "border-white/10 hover:bg-white/5"
            : "border-gray-200 hover:bg-gray-50"
        } transition-colors`}
      >
        {domain && (
          <p
            className={`text-[10px] uppercase tracking-wider font-semibold truncate ${
              isDark ? "text-gray-500" : "text-gray-400"
            }`}
          >
            {domain}
          </p>
        )}
        {headline && (
          <p
            className={`text-sm font-bold line-clamp-2 ${
              isDark ? "text-white" : "text-gray-900"
            }`}
          >
            {headline}
          </p>
        )}
        {description && (
          <p
            className={`text-xs leading-relaxed line-clamp-2 ${
              isDark ? "text-gray-400" : "text-gray-600"
            }`}
          >
            {description}
          </p>
        )}
        {buttonText && (
          <p
            className={`text-xs font-semibold mt-0.5 ${
              isDark ? "text-orange-400" : "text-orange-600"
            }`}
          >
            {buttonText}
          </p>
        )}
      </div>
    </a>
  );
}
